import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const WhyJoinPage = () => {
  const benefits = [
    { 
      icon: '', 
      title: 'Be Part of a Good Cause', 
      description: 'Petition.io is built around a living DAO structure. Petitioners can join the Petitioner DAO to vote on protocol upgrades, new features, yield allocations, and strategic partnerships. Whether you\'re a developer, activist, or donor, your participation shapes how Petition.io evolves—from its core funding logic to its social impact campaigns.', 
      image: '/images/Be-Part-of-a-Good-Cause.jpg' 
    },
    { 
      icon: '', 
      title: 'Help Shape Petition.io\'s Future', 
      description: 'Community members who help organize, verify, and support petitions become directly connected to humanitarian efforts across the world. By participating in the Petition.io ecosystem, you help amplify the voices of vulnerable communities, strengthen global transparency, and drive real-world impact through decentralized coordination.', 
      image: '/images/Help-Shape-Petition.io-Future.jpg' 
    },
   { 
      icon: '', 
      title: 'Ethereum-Based Recognition', 
      description: 'Active contributors — including petition organizers, verifiers, and DAO participants — may receive volunteer crypto appreciation gifts as symbolic expressions of gratitude for supporting the Petition.io ecosystem and upholding its integrity. They are not tied to investment, profit, or performance — they simply recognize meaningful contributions to humanitarian coordination.', 
      image: '/images/image10.jpg' 
    },

    { 
      icon: '', 
      title: 'Download Verified Petitions', 
      description: 'Every petition generates a verifiable PDF with its title, description, and blockchain transaction hash. These files serve as proof that petitions were created and supported transparently—a foundation for future legal recognition and digital trust.', 
      image: '/images/Download-verified-petitions.jpg' 
    },
    { 
      icon: '', 
      title: 'Test Funding & Impact', 
      description: 'Experience how decentralized crowdfunding feels in action. You can fund petitions using ETH, simulate real campaigns, and see how blockchain transparency ensures that every vote and contribution is recorded fairly and permanently.', 
      image: '/images/Test-Funding-and-impact.jpg' 
    },
    { 
      icon: '', 
      title: '"Trustless" Transparency', 
      description: 'All petition activity—votes, funding, and signatures—is secured by smart contracts. There\'s no central authority or private control. Everything is transparent, auditable, and built to ensure fairness for everyone involved.', 
      image: '/images/image13.jpg' 
    }
  ];

  return (
    <>
      <Helmet>
        <title>Why Join - Petition.io | Decentralized Petition Platform Benefits</title>
        <meta name="description" content="Join the Petitioner DAO, earn Ethereum dividends, shape the platform's future, and be part of decentralized social impact movement." />
        <meta property="og:title" content="Why Be a Petitioner - Join the Movement" />
        <meta property="og:description" content="Earn dividends, govern the platform, and create positive change" />
        <link rel="canonical" href="https://petition.io/why-join" />
      </Helmet>

      <div className="min-h-screen bg-white py-12 sm:py-16 md:py-20 pt-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="inline-block mb-6"
            >
            </motion.div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-neutral-dark mb-6 font-lato">
              Why Be a Petitioner?
            </h1>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="max-w-5xl mx-auto mb-12 sm:mb-16 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl"
          >
            <img 
              src="/images/why-be-a-petitioner.jpg" 
              alt="Join the Movement"
              className="w-full h-64 sm:h-80 md:h-96 object-cover object-center"
            />
          </motion.div>
          <br /> 
          <center> 
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-montserrat">
              Join the movement for decentralized social impact
            </p>
          </center>
          <br />
          <br />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                whileHover={{ y: -10, boxShadow: '0 20px 40px rgba(0, 102, 204, 0.2)' }}
                className="bg-gradient-to-br from-white to-blue-50/30 rounded-xl sm:rounded-2xl overflow-hidden shadow-card border border-gray-100"
              >
                <div className="relative h-40 sm:h-48 overflow-hidden">
                  <img 
                    src={benefit.image} 
                    alt={benefit.title}
                    className="w-full h-full object-cover object-center transform hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                </div>
                <div className="p-6 sm:p-8">
                  {benefit.icon && <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">{benefit.icon}</div>}
                  <h3 className="text-lg sm:text-xl font-bold text-neutral-dark mb-2 sm:mb-3">{benefit.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-12 sm:mt-16 md:mt-20 bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 shadow-2xl border border-gray-100"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 sm:mb-8 text-center text-primary-blue">
              Advantages & Opportunity
            </h2>
            <div className="max-w-4xl mx-auto space-y-6">
              <p className="text-lg sm:text-xl md:text-2xl text-neutral-dark leading-relaxed px-4">
                Petition.io is the first step toward a new kind of civic technology—one that blends community action with blockchain security.
              </p>
              <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed px-4">
                By joining now, you're not just testing software; you're helping shape the future of decentralized petitions, public funding, and digital advocacy.
              </p>
              <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed px-4">
                Petition.io gives early Petitioners the chance to be part of the next major movement in decentralized social impact—a platform owned by the people, for the people.
              </p>
            </div>
            <div className="text-center mt-8">
              <Link to="/dao">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 sm:px-10 py-4 sm:py-5 bg-gradient-to-r from-primary-blue to-primary-light text-white text-lg sm:text-xl font-bold rounded-xl shadow-xl hover:shadow-2xl inline-flex items-center"
                >
                  <span>Learn About Petitioner DAO</span>
                  <svg className="ml-3 w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default WhyJoinPage;
